// The post-processing chain: render → N8AO (by tier) → sanitize → one merged effect pass
// (bloom, extra effects such as trip and DoF, AgX tone mapping, vignette, grain, SMAA).
import { HalfFloatType, type Camera, type Scene, type WebGLRenderer } from 'three';
import {
  BloomEffect, EffectComposer, EffectPass, type Effect, NoiseEffect, BlendFunction, RenderPass,
  SMAAEffect, SMAAPreset, ToneMappingEffect, ToneMappingMode, VignetteEffect,
} from 'postprocessing';
import { N8AOPostPass } from 'n8ao';
import type { TierSettings } from '../quality';
import { SanitizeEffect } from './sanitize';

export interface PostChain {
  composer: EffectComposer;
  bloom: BloomEffect;
  toneMapping: ToneMappingEffect;
  vignette: VignetteEffect;
  /** Adds an effect before tone mapping (scene-referred). Rebuilds the effect pass. */
  addEffect(effect: Effect): void;
  removeEffect(effect: Effect): void;
  setCamera(camera: Camera): void;
  applyTier(settings: TierSettings): void;
  setSize(width: number, height: number): void;
  render(dt: number): void;
}

export function createPostChain(renderer: WebGLRenderer, scene: Scene, camera: Camera, tier: TierSettings): PostChain {
  const composer = new EffectComposer(renderer, { frameBufferType: HalfFloatType, multisampling: 0 });
  const renderPass = new RenderPass(scene, camera);
  const ao = new N8AOPostPass(scene, camera, 1, 1);
  ao.configuration.aoRadius = 2.5;
  ao.configuration.distanceFalloff = 1;
  ao.configuration.intensity = 2.5;
  ao.configuration.gammaCorrection = false; // tone mapping comes later in the chain

  const bloom = new BloomEffect({ mipmapBlur: true, luminanceThreshold: 0.9, luminanceSmoothing: 0.2, intensity: 0.8, radius: 0.7 });
  const toneMapping = new ToneMappingEffect({ mode: ToneMappingMode.AGX });
  const vignette = new VignetteEffect({ offset: 0.3, darkness: 0.45 });
  const grain = new NoiseEffect({ blendFunction: BlendFunction.OVERLAY, premultiply: true });
  grain.blendMode.opacity.value = 0.06;
  const smaa = new SMAAEffect({ preset: SMAAPreset.HIGH });
  const sanitize = new SanitizeEffect();
  let sanitizePass: EffectPass | null = null;
  const extra: Effect[] = [];
  let currentCamera = camera;
  let effectPass: EffectPass | null = null;
  let current = tier;

  const rebuild = () => {
    composer.removeAllPasses();
    renderPass.mainCamera = currentCamera;
    composer.addPass(renderPass);
    if (current.ao !== 'off') {
      ao.configuration.halfRes = current.ao === 'half';
      ao.setQualityMode(current.ao === 'half' ? 'Low' : 'Medium');
      composer.addPass(ao);
    }
    // its own pass: the bloom reads the pass input directly, so cleaning inside the merged pass is too late
    sanitizePass?.dispose();
    sanitizePass = new EffectPass(currentCamera, sanitize);
    composer.addPass(sanitizePass);
    effectPass?.dispose();
    const effects: Effect[] = [];
    if (current.bloom) effects.push(bloom);
    effects.push(...extra, toneMapping, vignette, grain);
    if (current.smaa) effects.push(smaa);
    effectPass = new EffectPass(currentCamera, ...effects);
    composer.addPass(effectPass);
  };
  rebuild();

  return {
    composer, bloom, toneMapping, vignette,
    addEffect(e) { if (!extra.includes(e)) { extra.push(e); rebuild(); } },
    removeEffect(e) { const i = extra.indexOf(e); if (i >= 0) { extra.splice(i, 1); rebuild(); } },
    setCamera(c) {
      if (c === currentCamera) return;
      currentCamera = c;
      (ao as unknown as { camera: Camera }).camera = c;
      rebuild();
    },
    applyTier(s) { current = s; rebuild(); },
    setSize(w, h) { composer.setSize(w, h, false); },
    render(dt) { composer.render(dt); },
  };
}
